import React, { useState } from 'react';
import TemplatesApi from '../../../api/templates-api';
import './TemplateViewModal.css';

const TemplateViewModal = ({ template, isOpen, onClose, onTemplateUpdate }) => {
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusCheckResult, setStatusCheckResult] = useState(null);
  const [statusCheckError, setStatusCheckError] = useState(null);

  if (!template) return null;

  const handleCheckApprovalStatus = async () => {
    setIsCheckingStatus(true);
    setStatusCheckResult(null);
    setStatusCheckError(null);

    try {
      const response = await TemplatesApi.private.checkTwilioApprovalStatus(template._id);
      if (response.data && response.data.data) {
        const updatedTemplate = response.data.data;
        setStatusCheckResult(updatedTemplate);
        
        // Update the template in the parent component if callback is provided
        if (onTemplateUpdate && typeof onTemplateUpdate === 'function') {
          onTemplateUpdate(updatedTemplate);
        }
      } else {
        setStatusCheckError('Invalid response format');
      }
    } catch (error) {
      console.error('Error checking approval status:', error);
      setStatusCheckError(error.response?.data?.message || 'Failed to check approval status');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleClose = () => {
    setStatusCheckResult(null);
    setStatusCheckError(null);
    onClose();
  };

  return (
    <div
      className={`template-modal-overlay ${isOpen ? "visible" : ""}`}
      onClick={handleClose}
    >
      <div 
        className="template-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="template-modal-header">
          <h3 className="template-modal-title">Template Details</h3>
          <button
            className="template-modal-close"
            onClick={handleClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="template-modal-body">
          <div className="template-detail-grid">
            {/* Left Column - Metadata */}
            <div className="template-metadata-section">
              <h4 className="template-section-title">Template Information</h4>
              
              <div className="template-detail-item">
                <div className="template-detail-label">Name</div>
                <div className="template-detail-value">{template.name}</div>
              </div>
              
              <div className="template-detail-item">
                <div className="template-detail-label">Platform</div>
                <div className="template-detail-value">{template.templatePlatform}</div>
              </div>
              
              <div className="template-detail-item">
                <div className="template-detail-label">Template ID</div>
                <div className="template-detail-value template-id">{template._id}</div>
              </div>

              {template.metadata && (
                <>
                  <div className="template-detail-item">
                    <div className="template-detail-label">Approval Status</div>
                    <div className="template-detail-value">
                      <span className={`template-approval-badge ${template.metadata.approvalStatus}`}>
                        {template.metadata.approvalStatus}
                      </span>
                      {template.templatePlatform === 'twilio' && (
                        <button
                          className="template-check-status-button"
                          onClick={handleCheckApprovalStatus}
                          disabled={isCheckingStatus}
                          style={{ marginLeft: '10px' }}
                        >
                          {isCheckingStatus ? 'Checking...' : 'Check Status'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="template-detail-item">
                    <div className="template-detail-label">Version</div>
                    <div className="template-detail-value">{template.metadata.version}</div>
                  </div>
                  
                  <div className="template-detail-item">
                    <div className="template-detail-label">Language</div>
                    <div className="template-detail-value">{template.metadata.language}</div>
                  </div>
                  
                  <div className="template-detail-item">
                    <div className="template-detail-label">Category</div>
                    <div className="template-detail-value">
                      <span className="template-category-badge">
                        {template.metadata.category}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="template-detail-item">
                <div className="template-detail-label">Applicable For</div>
                <div className="template-detail-value template-tags">
                  {template.applicableFor.map((platform, index) => (
                    <span key={index} className="template-platform-tag">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right Column - Content */}
            <div className="template-content-section">
              <h4 className="template-section-title">Template Content & Variables</h4>
              
              <div className="template-detail-item">
                <div className="template-detail-label">Template Message</div>
                <div className="template-detail-value template-content">
                  {template.value || 'No content available'}
                </div>
              </div>
              
              <div className="template-detail-item">
                <div className="template-detail-label">Variables / Fields</div>
                <div className="template-detail-value template-tags">
                  {template.fields && template.fields.length > 0 ? (
                    template.fields.map((field, index) => (
                      <span key={index} className="template-tag">
                        {field}
                      </span>
                    ))
                  ) : (
                    <span className="no-variables">No variables defined</span>
                  )}
                </div>
              </div>

              {template.metadata?.ctaButtons && template.metadata.ctaButtons.length > 0 && (
                <div className="template-detail-item">
                  <div className="template-detail-label">CTA Buttons</div>
                  <div className="template-detail-value">
                    {template.metadata.ctaButtons.map((btn, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        marginBottom: '6px',
                        background: '#e3f2fd',
                        borderRadius: '6px',
                        fontSize: '0.9em'
                      }}>
                        <i className="fas fa-external-link-alt" style={{ color: '#1976d2' }}></i>
                        <span style={{ fontWeight: 500 }}>{btn.title}</span>
                        <span style={{ color: '#666', fontSize: '0.85em', wordBreak: 'break-all' }}>{btn.url}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Check Results */}
              {statusCheckResult && (
                <div className="template-status-check-result">
                  <div className="template-detail-label">Latest Status Check</div>
                  <div className="template-detail-value">
                    <span className={`template-approval-badge ${statusCheckResult.metadata.approvalStatus}`}>
                      {statusCheckResult.metadata.approvalStatus}
                    </span>
                    {statusCheckResult.metadata.rejectionReason && (
                      <div className="template-rejection-reason">
                        <strong>Rejection Reason:</strong> {statusCheckResult.metadata.rejectionReason}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {statusCheckError && (
                <div className="template-status-check-error">
                  <div className="template-detail-label">Status Check Error</div>
                  <div className="template-detail-value error-text">
                    {statusCheckError}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="template-modal-footer">
          <button
            className="bouncy-button"
            onClick={handleClose}
          >
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateViewModal; 